import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import {
  Alert,
  Button,
  Divider,
  FormControl,
  Grid,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Head from "next/head";
import { useRouter } from "next/router";
import { createContext, useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "#next/redux/reduxHooks";
import {
  SearchBuilderUIState,
  TSearchBuilderMode,
  getSearchFromState,
  selectCanSave,
  selectFilterSettings,
  selectHasUnsavedChanges,
  selectRootBlock,
  selectSearchName,
  setFilterSettings,
  setSearchName,
  setSearchWasCreated,
  setSearchWasUpdated,
  resetNewSearch,
} from "#next/redux/searchBuilderSlice";
import theme from "#next/theme";
import useWarningOnExit from "#next/third-party/useWarningOnExit";
import {
  TFilterSettings,
  zCreateSavedSearchInput,
  zRunSavedSearchInput,
  zUpdateSavedSearchInput,
} from "#next/utils/shared";
import { RouterOutput, trpc } from "#next/utils/trpc";
import { ButtonWithPopper } from "#next/components/reusable/ButtonWithPopper";
import { ConfirmDialog } from "#next/components/reusable/ConfirmDialog";
import { CreateSubscriptionModal } from "#next/components/modals/EditSubscriptionModal";
import { RenderQuery } from "#next/components/base/RenderQuery";
import { SearchFilter } from "#next/components/search-builder/SearchFilter";
import {
  GroupBlock,
  TooltipForDisabledComponent,
  blockCanGenerateSearch,
} from "#next/components/search-builder/Block";
import { NarrativesModal } from "#next/components/modals/NarrativesModal";
import { SharingModal } from "#next/components/modals/SharingModal";
import { Toolbar } from "#next/components/search-builder/Toolbar";
import { SaveButton } from "#next/components/reusable/SaveButton";
import { RightSideDrawer } from "#next/components/search-builder/RightSideDrawer";
import { TPreviewState } from "#next/components/search-builder/shared";

export function DeleteConfirmDialog({
  onDeleteConfirmed,
  onCancel,
  isOpen,
}: {
  onDeleteConfirmed: () => void;
  onCancel: () => void;
  isOpen: boolean;
}) {
  const contextText = (
    <Typography>
      Are you sure you want to <b>permanently delete</b> this project? This
      action cannot be undone.
    </Typography>
  );
  const confirmButton = (
    <Button onClick={onDeleteConfirmed} color="error">
      Delete
    </Button>
  );
  const cancelButton = (
    <Button onClick={onCancel} autoFocus>
      Cancel
    </Button>
  );
  return (
    <ConfirmDialog
      title="Permanently delete this project?"
      isOpen={isOpen}
      onClose={onCancel}
      contentText={contextText}
      confirmButton={confirmButton}
      cancelButton={cancelButton}
    />
  );
}

const DeleteButton = ({ searchId }: { searchId: string }) => {
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const router = useRouter();
  const trpcUtils = trpc.useUtils();

  const deleteSearch = trpc.savedSearch.delete.useMutation({
    onSuccess: (data) => {
      trpcUtils.savedSearch.listSidebar.setData(undefined, data.sidebar);
      trpcUtils.savedSearch.list.setData(undefined, data.all);
      router.push("/catalog");
    },
  });

  const handleDeleteConfirmed = () => {
    deleteSearch.mutate({ savedSearchId: searchId });
    setConfirmDialogOpen(false);
  };

  return (
    <RenderQuery
      useQuery={trpc.savedSearch.canDelete}
      input={{ savedSearchId: searchId }}
      render={(data) => {
        if (data.canDelete) {
          return (
            <>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => setConfirmDialogOpen(true)}
              >
                Delete
              </Button>
              <DeleteConfirmDialog
                isOpen={isConfirmDialogOpen}
                onCancel={() => setConfirmDialogOpen(false)}
                onDeleteConfirmed={handleDeleteConfirmed}
              />
            </>
          );
        } else {
          return (
            <TooltipForDisabledComponent disabledTooltipText={data.reason}>
              <Button size="small" disabled variant="contained">
                Delete
              </Button>
            </TooltipForDisabledComponent>
          );
        }
      }}
    />
  );
};

export const SavedSearchIdContext = createContext<
  | {
      savedSearchId: string;
      searchName: string;
      mode: TSearchBuilderMode;
      filterSettings: TFilterSettings;
    }
  | {
      savedSearchId: null;
      searchName: undefined;
      mode: "edit";
      filterSettings: TFilterSettings;
    }
  | Record<string, never>
>({});

// Helper hook so that code using selectors doesn't need to be aware of which
// search it's operating on.
export function useSavedSearchSelector<F>(
  selector: (state: SearchBuilderUIState) => F,
) {
  const { savedSearchId, mode } = useContext(SavedSearchIdContext);
  return useAppSelector((state) => {
    const search = getSearchFromState(
      state.searchBuilderReducer,
      mode,
      savedSearchId,
    );
    return selector(search);
  });
}

export function Builder({
  initialFilterSettings,
}: {
  initialFilterSettings?: TFilterSettings;
}) {
  const { savedSearchId, searchName, filterSettings } =
    useContext(SavedSearchIdContext);

  const [previewState, setPreviewState] = useState<TPreviewState>({
    groupBlock: null,
    filterSettings: filterSettings ?? initialFilterSettings,
    isPreviewing: false,
  });

  const block = useSavedSearchSelector(selectRootBlock);
  const dispatch = useAppDispatch();
  const router = useRouter();
  // See note about removing Undo eventually if no-one wants/needs it
  // const { canUndo, canRedo } = useAppSelector(selectUndoRedoStatus);
  const trpcUtils = trpc.useUtils();
  const createSearch = trpc.savedSearch.create.useMutation();
  const updateSearch = trpc.savedSearch.update.useMutation();

  useEffect(() => {
    if (initialFilterSettings) {
      dispatch(resetNewSearch({ initialFilterSettings }));
    }
  }, [dispatch, initialFilterSettings]);

  const sharingSettingsResponse = trpc.savedSearch.sharingSettings.useQuery(
    { savedSearchId: savedSearchId || "" },
    {
      // TODO: Rather than using `enabled` you might be able to create
      // a small conditionally-rendered sharing component that can always call the hook
      enabled: !!savedSearchId,
    },
  );

  let sharingText = "";
  let sharingIcon = <></>;

  if (sharingSettingsResponse.status === "success") {
    const { data } = sharingSettingsResponse;
    const usersWithAccess = data.userSharing.filter(
      (user) => !!user.permission,
    );
    if (data.sharedWithOrg) {
      sharingText = "Shared with whole organization";
      sharingIcon = <WorkspacesIcon sx={{ mr: 1 }} />;
    } else if (usersWithAccess.length) {
      sharingText = `Shared with ${usersWithAccess
        .map((user) => user.name)
        .sort()
        .join(", ")}`;
      sharingIcon = <GroupsIcon sx={{ mr: 1 }} />;
    } else {
      sharingText = "Private to you";
      sharingIcon = <PersonIcon sx={{ mr: 1 }} />;
    }
  }

  const currentSearchName = useSavedSearchSelector(selectSearchName);
  const {
    settings: currentFilterSettings,
    settingsAreValid: currentFilterSettingsAreValid,
  } = useSavedSearchSelector(selectFilterSettings);

  let { reason } = blockCanGenerateSearch(block, currentFilterSettingsAreValid);
  const canSave = useSavedSearchSelector(selectCanSave) && !!currentSearchName;
  if (!currentSearchName) {
    reason = "Please enter a project name.";
  }

  const [sharingModalIsOpen, setSharingModalIsOpen] = useState(false);
  const [narrativesModalIsOpen, setNarrativesModalIsOpen] = useState(false);
  const [subscribeModalState, setSubscribeModalState] = useState<
    "closed" | "open" | "saved"
  >("closed");
  const [error, setError] = useState<string>();

  const saveClick = () => {
    const afterSave = (
      result: RouterOutput["savedSearch"]["create"],
      searchWasCreated: boolean,
    ) => {
      // TODO: Rather than invalidate the list we should return new data and update
      const savedSearchId = result.getById.id;
      trpcUtils.savedSearch.listSidebar.setData(undefined, result.sidebar);
      trpcUtils.savedSearch.list.setData(undefined, result.list);
      trpcUtils.savedSearch.getById.setData({ savedSearchId }, result.getById);
      trpcUtils.savedSearch.runSavedSearch.invalidate(undefined, {
        predicate: (query) => {
          // We need to invalidate all queries with this ID, regardless of timeRange param
          // See https://trpc.io/docs/migrate-from-v9-to-v10#query-key-changes
          if (
            query.queryKey[0][0] === "savedSearch" &&
            query.queryKey[0][1] === "runSavedSearch"
          ) {
            const params = zRunSavedSearchInput.parse(query.queryKey[1]?.input);
            return params.savedSearchId === savedSearchId;
          }
          return false;
        },
      });
      if (searchWasCreated) {
        dispatch(setSearchWasCreated()); // Prevent the "you have unsaved changes" dialog from showing
        router.push(`/project-editor?projectId=${savedSearchId}`, undefined);
      } else {
        dispatch(setSearchWasUpdated({ savedSearchId }));
      }
    };

    if (savedSearchId) {
      updateSearch.mutate(
        zUpdateSavedSearchInput.parse({
          name: currentSearchName,
          id: savedSearchId,
          groupBlock: block,
          narratives: new Set(),
          filterSettings: currentFilterSettings,
        }),
        {
          onSuccess: (result) => {
            afterSave(result, false);
          },
        },
      );
    } else {
      createSearch.mutate(
        zCreateSavedSearchInput.parse({
          name: currentSearchName,
          groupBlock: block,
          narratives: new Set(),
          filterSettings: currentFilterSettings,
        }),
        {
          onSuccess: (result) => afterSave(result, true),
          onError: (error) => setError(error.message),
        },
      );
    }
  };

  const hasUnsavedChanges = useSavedSearchSelector(selectHasUnsavedChanges);

  useWarningOnExit(
    hasUnsavedChanges,
    "You have unsaved changes. Navigating away without saving will cause these to be lost. Click OK to continue navigating and lose changes.",
  );

  return (
    <div style={{ flexGrow: 1 }}>
      <Head>
        <title>
          {(searchName ? `Editing "${searchName}"` : `Create Project`) +
            " | Pyrra"}
        </title>
      </Head>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(undefined)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError(undefined)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Grid container>
        <Grid
          item
          container
          sx={{
            borderBottom: "0.5pt solid #00c9b9",
            paddingBottom: 0.5,
            paddingTop: 0.5,
            mb: 0.5,
            borderWidth: 0.1,
            backgroundColor: `${theme.palette.toolbar.main}`,
          }}
        >
          <Toolbar>
            <Grid container direction="column">
              <Grid container direction="row" alignItems="top">
                <Grid item sx={{ mr: 1 }}>
                  <FormControl size="small">
                    <TextField
                      id="save-search-label"
                      size="small"
                      value={currentSearchName ?? ""}
                      onChange={(event) => {
                        dispatch(
                          setSearchName({
                            name: event.target.value,
                            savedSearchId,
                          }),
                        );
                      }}
                      // Setting the height of the input component is a hack to get it to line
                      // up with the platform selector. The difference is caused by the platform
                      // selector using an inner typography element, which is a fraction of an
                      // em higher than the default here
                      // HACK: This should really be sorted in SearchFilter so that other
                      // components don't have to adapt to it.
                      inputProps={{ style: { height: "1.5em" } }}
                      label="Project Name"
                    />
                  </FormControl>
                </Grid>
                <Grid item>
                  <SearchFilter
                    initialSettings={filterSettings}
                    onSettingsChange={({ settings, settingsAreValid }) => {
                      dispatch(
                        setFilterSettings({
                          settings,
                          settingsAreValid,
                          savedSearchId,
                        }),
                      );
                    }}
                  />
                </Grid>
                <Grid item sx={{ ml: 1 }}>
                  <SaveButton
                    disabled={!canSave}
                    disabledReason={reason}
                    onSaveClick={saveClick}
                    mutation={savedSearchId ? updateSearch : createSearch}
                  />
                </Grid>
                <Grid item>
                  <Grid container direction="row">
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ ml: 2, background: theme.palette.cyan.main }}
                    />
                    <Grid item sx={{ ml: 2 }}>
                      <Grid container alignItems="center" wrap="nowrap">
                        <Grid item sx={{ display: "flex" }}>
                          <Tooltip title={sharingText}>{sharingIcon}</Tooltip>
                        </Grid>
                        <Grid item>
                          <TooltipForDisabledComponent
                            disabledTooltipText={
                              savedSearchId ? "" : "Save project first"
                            }
                          >
                            <Button
                              size="small"
                              disabled={!savedSearchId}
                              variant="contained"
                              onClick={() => {
                                setSharingModalIsOpen(true);
                              }}
                            >
                              Sharing Settings
                            </Button>
                          </TooltipForDisabledComponent>
                        </Grid>
                        {savedSearchId && sharingModalIsOpen && (
                          <SharingModal
                            open={sharingModalIsOpen}
                            searchId={savedSearchId}
                            onClose={() => {
                              setSharingModalIsOpen(false);
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                    <Grid item sx={{ ml: 2 }}>
                      <Grid container alignItems="center">
                        <TooltipForDisabledComponent
                          disabledTooltipText={
                            savedSearchId ? "" : "Save project first"
                          }
                        >
                          <Button
                            size="small"
                            disabled={!savedSearchId}
                            variant="contained"
                            onClick={() => {
                              setNarrativesModalIsOpen(true);
                            }}
                          >
                            Manage Narratives
                          </Button>
                        </TooltipForDisabledComponent>
                        {savedSearchId && narrativesModalIsOpen && (
                          <NarrativesModal
                            narrativesModalIsOpen={narrativesModalIsOpen}
                            searchId={savedSearchId}
                            setNarrativesModalIsOpen={setNarrativesModalIsOpen}
                          />
                        )}
                      </Grid>
                    </Grid>
                    <Grid item sx={{ paddingRight: "1em", ml: 2 }}>
                      <Grid container alignItems="center">
                        <ButtonWithPopper
                          showPopper={subscribeModalState === "saved"}
                          popperText="Subscribed"
                          renderButton={(setPopperAnchorEl) => {
                            return (
                              <>
                                <Button
                                  size="small"
                                  disabled={!savedSearchId}
                                  variant="contained"
                                  sx={{
                                    backgroundColor: "secondary",
                                  }}
                                  onClick={(event) => {
                                    setSubscribeModalState("open");
                                    setPopperAnchorEl(event.currentTarget);
                                  }}
                                >
                                  Subscribe
                                </Button>
                                {savedSearchId && (
                                  <CreateSubscriptionModal
                                    open={subscribeModalState === "open"}
                                    savedSearchId={savedSearchId}
                                    onClose={(didSave) => {
                                      setSubscribeModalState(
                                        didSave ? "saved" : "closed",
                                      );
                                    }}
                                  />
                                )}
                              </>
                            );
                          }}
                        />
                      </Grid>
                    </Grid>
                    {savedSearchId && (
                      <>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ background: theme.palette.cyan.main }}
                        />
                        <Grid item sx={{ paddingRight: "1em", ml: 2 }}>
                          <DeleteButton searchId={savedSearchId} />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Toolbar>
        </Grid>
        <Grid item sx={{ maxWidth: "1400px" }}>
          <GroupBlock
            blockId={block.internalId}
            setPreviewState={setPreviewState}
          />
        </Grid>
      </Grid>
      {previewState.isPreviewing && (
        <RightSideDrawer
          previewState={previewState}
          setPreviewState={setPreviewState}
        />
      )}
    </div>
  );
}
